import {
	AdminViewOrderWrapperPage,
	Severity,
} from "node-library/dist/constants";
import {
	FrontendClient,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrder,
	FrontendOrganization,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import React, { SetStateAction, useEffect, useState } from "react";

import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";

import DiscardOrderConfirmModal from "../../common-components/modals/discardOrderConfirmModal";
import { YellowCrossIcon } from "../../common-components/toast/warnIcons";
import NewOrEditOrder from "../mainPages/newOrEditOrderPage";
import AdminViewOrderReportPage from "../mainPages/viewOrders/admin_view/adminViewClientOrderReportPage";
import OrderDetailsContent from "../mainPages/viewOrders/orderDetails/orderDetailsContent";
import OrderDetailsContentSkeletonUI from "../mainPages/viewOrders/skeletonUI/orderDetailsContentSkeleton";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;

	orderId: string;
	user: FrontendUserInfo | undefined;

	navigateToPreviousPage: () => void;
	goToViewOrderPage: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewOrderDetailsContentWrapper(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		orderId,
		navigateToPreviousPage,

		setUnsavedChanges,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);

	const [selectedOrder, setSelectedOrder] = useState<FrontendOrder>();
	const [orderTestCategory, setOrderTestCategory] =
		useState<FrontendCompanyTestCategory>();
	const [orderClient, setOrderClient] = useState<FrontendClient>();

	const [selectedOrderOrganization, setSelectedOrderOrganization] =
		useState<FrontendOrganization>();

	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	const [cocPreviewUrl, setCocPreviewUrl] = useState<string>();

	const [currentPageIndex, setCurrentPageIndex] =
		useState<AdminViewOrderWrapperPage>(
			AdminViewOrderWrapperPage.ORDER_DETAILS,
		);

	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {},
	);

	useEffect(() => {
		async function loadOrderDetails() {
			setLoading(true);
			try {
				const response = await sendGETRequest("/orders/get-order-by-id", {
					orderId,
				});
				const order: FrontendOrder = response?.data?.order;
				setSelectedOrder(order);

				const client: FrontendClient = response.data.client;
				setOrderClient(client);

				const organization: FrontendOrganization = response.data.organization;
				setSelectedOrderOrganization(organization);

				const orderTestCategory: FrontendCompanyTestCategory =
					props.company.companyTestCategories.find((category) => {
						return category.testCategoryId === order.orderCategoryId;
					}) as FrontendCompanyTestCategory;
				setOrderTestCategory(orderTestCategory);

				const specDirectories: Array<FrontendSampleSpecificationDirectory> =
					response?.data?.specDirectories;
				setSpecDirectories(specDirectories);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			} finally {
				setLoading(false);
			}
		}

		if (orderId) {
			loadOrderDetails();
		}
	}, [orderId]);

	function gotoOrderDetailsPage() {
		setCurrentPageIndex(AdminViewOrderWrapperPage.ORDER_DETAILS);
	}

	function goToEditOrderPage() {
		setCurrentPageIndex(AdminViewOrderWrapperPage.EDIT_ORDER);
	}
	function goToOrderReportPage() {
		setCurrentPageIndex(AdminViewOrderWrapperPage.ORDER_REPORT);
	}

	async function deleteOrder(order: FrontendOrder) {
		//send a request to delete the temp order in the backend
		try {
			const response = await sendPOSTRequest("/orders/delete-order", {
				orderId: order.orderId,
			});
			//go to the submitted order page
			openSnackbar(
				translation("OrderDiscardSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />,
			);
			await navigateToPreviousPage();
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		}
	}

	function updateSpecification(updatedSpec: FrontendSampleSpecification) {
		setSelectedOrder((prevOrder) => {
			if (!prevOrder) return prevOrder;

			// 1. Map through the sample instances to find the one using this spec
			const updatedSampleInstances = prevOrder.sampleInstances.map(
				(instance) => {
					// Check if this instance has a specification and if it matches the one updated
					if (
						instance.specification?.specificationId ===
						updatedSpec.specificationId
					) {
						return {
							...instance,
							specification: updatedSpec, // Inject the new spec data
						};
					}
					return instance;
				},
			);

			// 2. Return the new order object with the updated instances array
			return {
				...prevOrder,
				sampleInstances: updatedSampleInstances,
			};
		});
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = !selectedOrder ? (
				<OrderDetailsContentSkeletonUI />
			) : (
				<OrderDetailsContent
					company={company}
					setCompany={setCompany}
					order={selectedOrder as FrontendOrder}
					orderTestCategory={orderTestCategory as FrontendCompanyTestCategory}
					sampleInstances={selectedOrder ? selectedOrder.sampleInstances : []}
					user={props.user}
					goToEditOrderPage={goToEditOrderPage}
					goBackToPreviousPage={navigateToPreviousPage}
					setDiscardOrderConfirmOpen={setDiscardOrderConfirmOpen}
					goToOrderReportPage={goToOrderReportPage}
					viewOrderCOC={() => {}}
					setProceedAction={setProceedAction}
					deleteOrder={deleteOrder}
					specDirectories={specDirectories}
					updateSpecification={updateSpecification}
				></OrderDetailsContent>
			);
			break;
		case 1:
			currentComponent = (
				<NewOrEditOrder
					company={props.company}
					setCompany={props.setCompany}
					orderTestCategory={orderTestCategory as FrontendCompanyTestCategory}
					user={props.user}
					client={orderClient}
					order={selectedOrder}
					gotoViewOrderPage={() => props.goToViewOrderPage()}
					setUnsavedChanges={setUnsavedChanges}
					goBackToPreviousPage={gotoOrderDetailsPage}
					selectedOrganization={
						selectedOrderOrganization as FrontendOrganization
					}
					orgInfoLoaded={false}
					clientLicenses={[]}
					organizationSamples={[]}
					organizationSpecifications={[]}
					organizationSpecDirectories={[]}
				></NewOrEditOrder>
			);
			break;
		case 2:
			currentComponent = (
				<AdminViewOrderReportPage
					selectedOrder={selectedOrder as FrontendOrder}
					goBackToViewOrdersPage={props.goToViewOrderPage}
					gotoOrderDetailsPage={gotoOrderDetailsPage}
				></AdminViewOrderReportPage>
			);
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{
				<>
					{currentComponent}

					<DiscardOrderConfirmModal
						open={discardOrderConfirmOpen}
						setOpen={setDiscardOrderConfirmOpen}
						proceedAction={proceedAction}
					></DiscardOrderConfirmModal>
				</>
			}
		</div>
	);
}
